import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Col, Row, Table, Tag, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";

import { isEqual } from "lodash";
import { DownOutlined, HolderOutlined, RightOutlined } from "@ant-design/icons";

import EditRole from "./EditRole";
import DeleteRole from "./DeleteRole";
import DraggableList from "../../../common/DraggableList";
import {
  getAllRoles,
  permissionUpdate,
} from "../../../actionCreator/roleManagementActionCreator";
import { useAlert } from "../../../context/AlertProvider";
import noFeature from "../../../assets/svg/icons/disable-permission.svg";
import { userRoles } from "../../../config";
import deleteIcon from "../../../assets/images/webToLead/delete.svg";
import editIcon from "../../../assets/svg/icons/edit-icon.svg";
import { LEAD_CUSTOM_FIELDS_MODULE } from "../../../utils/leadCustomFields";

const normalizeRolePermissions = (role) => ({
  ...role,
  permissions: {
    ...role.permissions,
    [LEAD_CUSTOM_FIELDS_MODULE]:
      role.permissions?.[LEAD_CUSTOM_FIELDS_MODULE] || [],
    WhatsApp: role.permissions?.WhatsApp || [],
  },
});

const RoleBasedPermissions = ({ isAdd, setAdd }) => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [active, setActive] = useState(userRoles?.admin);
  const [role, setRole] = useState();
  const [isDelete, setDelete] = useState();
  const roles = useSelector((state) => state.roleManagementReducer.roles);
  const loading = useSelector((state) => state.roleManagementReducer.loading);
  const [roleData, setRoles] = useState([]);
  const normalizedRoles = useMemo(
    () => (roles?.roles || []).map(normalizeRolePermissions),
    [roles],
  );

  const handleUpdate = async (payload, isSuccess = false) => {
    if (loading) return;
    const res = await dispatch(permissionUpdate(payload));
    dispatch(getAllRoles());
    if (res.status !== 200) {
      showAlert("error", "Failed to update permissions. Please try again.");
    }
    if (res.status === 200 && isSuccess) {
      showAlert("success", "Permissions updated successfully.");
    }
  };

  useEffect(() => {
    if (roles?.roles) {
      setRoles(normalizedRoles);
    }
  }, [roles, normalizedRoles]);

  const handleChecked = (v, row, action, id) => {
    setRoles(
      [
        ...roleData?.filter((d) => d?.name !== active),
        {
          ...roleData?.find((v) => v?.name === active),
          permissions: {
            ...roleData?.find((v) => v?.name === active)?.permissions,
            [row?.name]: v.target.checked
              ? [
                  ...roleData?.find((v) => v?.name === active)?.permissions?.[
                    row?.name
                  ],
                  {
                    createdDate: new Date(),
                    id: id,
                    internalName: null,
                    lastModifiedDate: new Date(),
                    name: action,
                  },
                ]
              : [
                  ...roleData
                    ?.find((v) => v?.name === active)
                    ?.permissions?.[
                      row?.name
                    ]?.filter((d) => d?.name !== action),
                ],
          },
        },
      ].sort((a, b) => a.position - b.position),
    );
  };

  const renderRoleCard = (role, index) => {
    return (
      <Tooltip
        title={
          !index
            ? "Admin role cannot be edit and reordered due to highest privilege."
            : null
        }
      >
        <div
          style={{ minHeight: 40 }}
          className={`cursor-pointer tw-group tw-mb-1 white-space-nowrap tw-px-3 hover:tw-bg-secondary-color d-flex text-capitalize align-items-center tw-justify-between  gap-10 tw-text-text-primary ${active?.toLowerCase() === role?.name?.toLowerCase() && "tw-bg-secondary-color tw-font-semibold"}`}
          onClick={() => setActive(role?.name)}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            <HolderOutlined className={`tw-text-[14px] tw-text-[#949494]`} />
            <span>{role?.name}</span>
          </div>
          {role?.name?.toLowerCase() !== "admin" && (
            <div
              className={`${active?.toLowerCase() === role?.name?.toLowerCase() ? "tw-flex" : "tw-hidden group-hover:tw-flex"} tw-gap-4 tw-items-center`}
            >
              <img
                src={editIcon}
                alt=""
                width={16}
                height={13}
                className="cursor-pointer"
                onClick={() => setRole(role)}
              />

              <img
                src={deleteIcon}
                alt=""
                width={16}
                height={13}
                className="cursor-pointer"
                onClick={() => setDelete(role)}
              />
            </div>
          )}
        </div>
      </Tooltip>
    );
  };

  const tooltipText = "Admin permissions are fixed and non-editable.";
  const isAdmin = active?.toLowerCase() === "admin";

  const columnExpand = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      render: (record, row) => ({
        children: (
          <div className="tw-flex tw-font-semibold tw-text-sm tw-text-text-primary tw-items-center tw-gap-3">
            <Checkbox
              disabled={isAdmin}
              onChange={(v) => {
                const activeRole = roleData.find((v) => v?.name === active);

                const updatedPermissions = { ...activeRole.permissions };

                row?.items?.forEach((item) => {
                  updatedPermissions[item.name] = v.target.checked
                    ? [
                        ...(activeRole.permissions[item.name] || []),
                        {
                          createdDate: new Date(),
                          id: 1,
                          internalName: null,
                          lastModifiedDate: new Date(),
                          name: "View",
                        },
                        {
                          createdDate: new Date(),
                          id: 2,
                          internalName: null,
                          lastModifiedDate: new Date(),
                          name: "Edit",
                        },
                        {
                          createdDate: new Date(),
                          id: 3,
                          internalName: null,
                          lastModifiedDate: new Date(),
                          name: "Create",
                        },
                        {
                          createdDate: new Date(),
                          id: 4,
                          internalName: null,
                          lastModifiedDate: new Date(),
                          name: "Delete",
                        },
                        {
                          createdDate: new Date(),
                          id: 5,
                          internalName: null,
                          lastModifiedDate: new Date(),
                          name: "Download",
                        },
                      ]
                    : [];
                });

                const finalData = [
                  ...roleData.filter((d) => d?.name !== active),
                  {
                    ...activeRole,
                    permissions: updatedPermissions,
                  },
                ].sort((a, b) => a.position - b.position);

                setRoles(finalData);
              }}
            />
            {row?.name}
            <Tag className="tw-rounded-full tw-bg-[#EBEBEB] tw-border-[#EBEBEB] tw-text-xs tw-font-medium tw-py-1 tw-px-2">
              {row?.items?.length} permissions
            </Tag>
          </div>
        ),
        props: {
          colSpan: 3,
        },
      }),
    },
    {
      title: "View (V)",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
      render: () => ({
        children: null,
        props: { colSpan: 0 },
      }),
    },
    {
      title: "Edit (E)",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
      render: () => ({
        children: null,
        props: { colSpan: 0 },
      }),
    },
    {
      title: "Create (C)",
      dataIndex: "permissions",
      key: "permissions",

      width: "14%",
    },
    {
      title: "Delete (D)",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
    },
    {
      title: "Download",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
    },
  ];

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
    },
    {
      title: "View (V)",
      dataIndex: "permissions",
      key: "permissions",
      render: (record, row) => {
        return (
          <Tooltip title={isAdmin ? tooltipText : null}>
            <Checkbox
              checked={record?.isView}
              disabled={isAdmin}
              onChange={(v) => {
                handleChecked(v, row, "View", 1);
              }}
            />
          </Tooltip>
        );
      },
      width: "14%",
    },
    {
      title: "Edit (E)",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
      render: (record, row) => {
        return (
          <>
            {["Company", "Executive"].includes(row?.name) ? (
              <img src={noFeature} alt="" className="w-20 h-20" />
            ) : (
              <Tooltip title={isAdmin ? tooltipText : null}>
                <Checkbox
                  checked={record?.isEdit}
                  disabled={isAdmin}
                  onChange={(v) => handleChecked(v, row, "Edit", 2)}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: "Create (C)",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
      render: (record, row) => {
        return (
          <>
            {["Company", "Executive"].includes(row?.name) ? (
              <img src={noFeature} alt="" className="w-20 h-20" />
            ) : (
              <Tooltip title={isAdmin ? tooltipText : null}>
                <Checkbox
                  checked={record?.isCreate}
                  disabled={isAdmin}
                  onChange={(v) => {
                    handleChecked(v, row, "Create", 3);
                  }}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: "Delete (D)",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
      render: (record, row) => {
        return (
          <>
            {["Company", "Executive"].includes(row?.name) ? (
              <img src={noFeature} alt="" className="w-20 h-20" />
            ) : (
              <Tooltip title={isAdmin ? tooltipText : null}>
                <Checkbox
                  checked={record?.isDelete}
                  disabled={isAdmin}
                  onChange={(v) => {
                    handleChecked(v, row, "Delete", 4);
                  }}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: "Download",
      dataIndex: "permissions",
      key: "permissions",
      width: "14%",
      render: (record, row) => {
        return (
          <>
            {!["Company", "Executive", "Leads"].includes(row?.name) ? (
              <img src={noFeature} alt="" className="w-20 h-20" />
            ) : (
              <Tooltip title={isAdmin ? tooltipText : null}>
                <Checkbox
                  checked={record?.isDownload}
                  disabled={isAdmin}
                  onChange={(v) => {
                    handleChecked(v, row, "Download", 5);
                  }}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const activeData = roleData?.find(
    (role) => role?.name === active,
  )?.permissions;

  const LeadAndOpportunity = {
    name: "Lead & Opportunity Operations",
    child: [
      "Leads",
      "Web To Leads",
      LEAD_CUSTOM_FIELDS_MODULE,
      "Unified Lead Timeline",
      "Lead Generation & Capture",
      "Kanban Pipeline View",
      "Executive",
      "Data-to-Lead Conversion",
    ],
  };

  const CommunicationAndOutreach = {
    name: "Communication & Outreach",
    child: [
      "Email Support",
      "Direct Email Integration",
      "Engagement & Communication",
      "Custom Forms (Branded Forms)",
      "WhatsApp",
    ],
  };

  const CampaignsAndIntelligence = {
    name: "Campaigns & Intelligence",
    child: [
      "Campaign Automation Rules",
      "Auto Lead Scoring & Tags",
      "Automation & Intelligence",
      "campaign_audiences",
    ],
  };

  const ReputationAndExperienceManagement = {
    name: "Reputation & Experience Management",
    child: ["Reputation Manager", "Unified Review Management"],
  };

  const IntegrationsAndDataAccess = {
    name: "Integrations & Data Access",
    child: [
      "Email Setup",
      "Integrated Data Sources",
      "API / Webhook Access",
      "Email Setup",
    ],
  };

  const AdministrationAndAccountControl = {
    name: "Administration & Account Control",
    child: [
      "User Control Management",
      "Support & Add-ons",
      "Onboarding Support",
      "Priority Support",
    ],
  };

  const data = activeData
    ? Object.entries(activeData).map(([moduleName, permissions]) => {
        const mappedPermissions = {
          isView: permissions?.length
            ? permissions.some((p) => p?.name === "View")
            : false,
          isEdit: permissions?.length
            ? permissions.some((p) => p?.name === "Edit")
            : false,
          isCreate: permissions?.length
            ? permissions.some((p) => p?.name === "Create")
            : false,
          isDelete: permissions?.length
            ? permissions.some((p) => p?.name === "Delete")
            : false,
          isDownload: permissions?.length
            ? permissions.some((p) => p?.name === "Download")
            : false,
        };
        return {
          name: moduleName,
          permissions: mappedPermissions,
        };
      })
    : [];

  const formattedData = [
    {
      key: "1",
      name: LeadAndOpportunity?.name,
      total: LeadAndOpportunity?.child?.length,
      items: data?.filter((d) => LeadAndOpportunity?.child?.includes(d?.name)),
    },
    {
      key: "2",
      name: CommunicationAndOutreach?.name,
      total: CommunicationAndOutreach?.child?.length,
      items: data?.filter((d) =>
        CommunicationAndOutreach?.child?.includes(d?.name),
      ),
    },
    {
      key: "3",
      name: CampaignsAndIntelligence?.name,
      total: CampaignsAndIntelligence?.child?.length,
      items: data?.filter((d) =>
        CampaignsAndIntelligence?.child?.includes(d?.name),
      ),
    },
    {
      key: "4",
      name: ReputationAndExperienceManagement?.name,
      total: ReputationAndExperienceManagement?.child?.length,
      items: data?.filter((d) =>
        ReputationAndExperienceManagement?.child?.includes(d?.name),
      ),
    },
    {
      key: "5",
      name: IntegrationsAndDataAccess?.name,
      total: IntegrationsAndDataAccess?.child?.length,
      items: data?.filter((d) =>
        IntegrationsAndDataAccess?.child?.includes(d?.name),
      ),
    },
    {
      key: "6",
      name: AdministrationAndAccountControl?.name,
      total: AdministrationAndAccountControl?.child?.length,
      items: data?.filter((d) =>
        AdministrationAndAccountControl?.child?.includes(d?.name),
      ),
    },
  ];

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col md={5}>
          <Card
            title={
              <div className="tw-uppercase tw-text-sm tw-font-semibold">
                Select Roles
              </div>
            }
            className="tw-card-body"
          >
            <DraggableList
              items={roleData}
              onChange={setRoles}
              renderItem={renderRoleCard}
            />
          </Card>
        </Col>
        <Col md={19}>
          <Card
            className="tw-card-body"
            title={
              <div className="tw-uppercase tw-text-sm tw-font-semibold">
                Select Role Based permission to Editor
              </div>
            }
          >
            <div className="tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-[#f0f0f0]"></div>
            <Table
              columns={columnExpand}
              dataSource={formattedData}
              rowKey="key"
              loading={loading}
              className="tw-permission-table"
              scroll={{ x: "max-content" }}
              expandable={{
                expandedRowRender: (record) => (
                  <div className="expand-row-animate">
                    <Table
                      columns={columns}
                      dataSource={record.items}
                      pagination={false}
                      rowKey="name"
                      className="tw-custom-child-table"
                    />
                  </div>
                ),
                rowExpandable: (record) => record.items?.length > 0,
                expandIconColumnIndex: 6,
                expandIcon: ({ expanded, onExpand, record }) =>
                  record.items?.length ? (
                    expanded ? (
                      <div className="tw-flex tw-items-center tw-justify-end">
                        <div className="tw-border tw-w-fit tw-border-solid tw-rounded-[10px] tw-px-2 tw-py-1 tw-border-border-secondary">
                          <DownOutlined
                            className="tw-text-text-primary"
                            onClick={(e) => onExpand(record, e)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="tw-flex tw-items-center tw-justify-end">
                        <div className="tw-border tw-w-fit tw-border-solid tw-rounded-[10px] tw-px-2 tw-py-1 tw-border-border-secondary">
                          <RightOutlined
                            className="tw-text-text-primary"
                            onClick={(e) => onExpand(record, e)}
                          />
                        </div>
                      </div>
                    )
                  ) : null,
              }}
              pagination={false}
            />

            <div className="d-flex justify-content-end tw-mb-4 tw-mr-4">
              <Button
                type="primary"
                disabled={
                  active === userRoles?.admin ||
                  isEqual(roleData, normalizedRoles)
                }
                className="theme-btn mt30"
                onClick={() =>
                  handleUpdate(
                    {
                      roles: roleData?.filter(
                        (d) => d?.name !== userRoles?.admin,
                      ),
                    },
                    true,
                  )
                }
              >
                Save Permissions
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
      <DeleteRole
        isOpen={isDelete}
        callback={() => setActive(userRoles?.admin)}
        handleCancel={() => setDelete()}
      />
      <EditRole
        role={role}
        isOpen={!!role || isAdd}
        closeModal={() => {
          setRole();
          setAdd(false);
        }}
        callback={(val) => {
          setActive(val);
        }}
      />
    </>
  );
};

export default RoleBasedPermissions;
